/**
 * ColorPicker Component
 * Displays design-system color palette for selection.
 * Does NOT allow free-form hex entry - only predefined colors.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Tooltip,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';

/**
 * Design system color palette for asset types/subtypes
 * These are the only colors allowed for configuration
 */
export const ASSET_COLOR_PALETTE = [
  // Primary brand colors
  { name: 'Orange (Primary)', value: '#EC4F36' },
  { name: 'Canary (Warning)', value: '#ECAE36' },
  
  // Status colors
  { name: 'Success Green', value: '#4CAF50' },
  { name: 'Error Red', value: '#CF3028' },
  { name: 'Info Blue', value: '#316EC0' },
  
  // Equipment/category colors
  { name: 'Blue', value: '#1E90FF' },
  { name: 'Teal', value: '#20B2AA' },
  { name: 'Purple', value: '#9370DB' },
  { name: 'Pink', value: '#FF69B4' },
  { name: 'Brown', value: '#8B4513' },
  { name: 'Gold', value: '#FFD700' },
  { name: 'Coral', value: '#FF6B35' },
  
  // Neutral shades
  { name: 'Dark Gray', value: '#666564' },
  { name: 'Medium Gray', value: '#808080' },
  { name: 'Slate', value: '#708090' },
];

/**
 * ColorPicker - A modal dialog for selecting colors from the design palette
 * 
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Called when dialog closes without selection
 * @param {function} onSelect - Called with the selected color value
 * @param {string} currentColor - Currently selected color (for highlighting)
 */
const ColorPicker = ({
  open,
  onClose,
  onSelect,
  currentColor = '',
}) => {
  const handleSelect = (colorValue) => {
    onSelect(colorValue);
    onClose();
  };

  const handleClear = () => {
    onSelect('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select Color</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose from the design system color palette
        </Typography>

        <Grid container spacing={1.5}>
          {ASSET_COLOR_PALETTE.map((color) => {
            const isSelected = currentColor?.toLowerCase() === color.value.toLowerCase();
            return (
              <Grid item key={color.value}>
                <Tooltip title={color.name} placement="top">
                  <Box
                    onClick={() => handleSelect(color.value)}
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: color.value,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: isSelected
                        ? '3px solid white'
                        : '2px solid transparent',
                      boxShadow: isSelected
                        ? `0 0 0 2px ${color.value}`
                        : 'none',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: `0 2px 8px ${color.value}80`,
                      },
                    }}
                  >
                    {isSelected && (
                      <CheckIcon sx={{ color: 'white', fontSize: 24 }} />
                    )}
                  </Box>
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {currentColor && (
          <Button onClick={handleClear} color="error">
            Clear Color
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

/**
 * ColorPickerButton - A button that shows current color and opens picker on click
 */
export const ColorPickerButton = ({
  color,
  onSelect,
  size = 44,
  label = 'Select Color',
  showLabel = false,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          onClick={() => setPickerOpen(true)}
          sx={{
            width: size,
            height: size,
            borderRadius: 1,
            backgroundColor: color || '#666564',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid',
            borderColor: color ? 'transparent' : 'divider',
            transition: 'all 0.15s ease',
            '&:hover': {
              borderColor: 'primary.main',
              transform: 'scale(1.05)',
            },
          }}
          title={label}
        >
          {!color && (
            <Typography variant="caption" sx={{ color: 'white', fontSize: 10 }}>
              +
            </Typography>
          )}
        </Box>
        {showLabel && (
          <Typography variant="body2" color="text.secondary">
            {color || 'No color set'}
          </Typography>
        )}
      </Box>
      <ColorPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={onSelect}
        currentColor={color}
      />
    </>
  );
};

/**
 * ColorSwatch - A simple color swatch display (non-interactive)
 */
export const ColorSwatch = ({ color, size = 16 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: color || '#666564',
      flexShrink: 0,
    }}
  />
);

export default ColorPicker;
